/**
 * Configuración de la vista de lista "Categorías" (metadata-driven).
 * Nueva lista = esta config + data provider; la estructura la renderiza ListView.
 */

import { ProductIcon } from '@/lib/polaris-icons';
import type { ListViewConfig } from '@/types/listView.types';
import { getStatusBadgeTone } from '@/types/listView.types';
import { ROUTES } from '@/config/routes.config';

export interface CategoryListItem {
  id: string;
  name: string;
  icon: string;
  image_url?: string;
  description?: string;
  display_order: number;
  is_active: boolean;
  product_count?: number;
  /** Slug para URL de edición (ej. bebidas) */
  slug: string;
}

export type NavigateFn = (path: string) => void;

/**
 * Devuelve la config de la lista de categorías.
 * Recibe navigate para que las acciones (crear, editar, empty state) usen React Router.
 */
export function getCategoriesListConfig(navigate: NavigateFn): ListViewConfig<CategoryListItem> {
  return {
    title: 'Categorías',
    titleIcon: ProductIcon,
    onBack: () => navigate(ROUTES.PRODUCTS),
    backAccessibilityLabel: 'Volver a Productos',
    primaryAction: {
      onAction: () => navigate(ROUTES.PRODUCTS_CATEGORIES_NEW),
    },
    resourceName: {
      singular: 'categoría',
      plural: 'categorías',
    },
    columns: [
      {
        key: 'image_url',
        title: 'Imagen',
        type: 'thumbnail',
        imageKey: 'image_url',
        emptyKey: 'icon',
        altKey: 'name',
      },
      {
        key: 'name',
        title: 'Nombre',
        type: 'text',
        fontWeight: 'medium',
      },
      {
        key: 'product_count',
        title: 'Productos',
        type: 'number',
        format: (value, item) =>
          `${value ?? 0} ${(value ?? 0) === 1 ? 'producto' : 'productos'}`,
      },
      {
        key: 'is_active',
        title: 'Estado',
        type: 'badge',
        map: { true: 'Activa', false: 'Inactiva' },
        toneFromValue: getStatusBadgeTone,
      },
      {
        key: 'actions',
        title: 'Acciones',
        type: 'actions',
        editRoute: '/products/categories/:slug/edit',
        editLabel: 'Editar',
        deleteLabel: 'Eliminar',
        delete: true,
      },
    ],
    emptyState: {
      heading: 'No hay categorías creadas',
      description: 'Crea categorías para organizar tus productos.',
      image: 'https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png',
      action: {
        content: 'Agregar categoría',
        onAction: () => navigate(ROUTES.PRODUCTS_CATEGORIES_NEW),
      },
    },
    bulkActions: [
      { content: 'Exportar datos', actionKey: 'export' },
      // "Eliminar" lo inyecta la plantilla vía prop `bulkDelete` (modal + toast integrados).
    ],
    idKey: 'id',
  };
}
